<template>
  <div>
    <custom-toolbar
      :total="$store.getters['reductCode/total']"
      :title="$t('reduction_codes')"
      :add="'Nouveau code de réduction'"
      @refresh="getList"
      @add="openForm"
    />
    <v-card class="mb-6 mt-6">
      <v-alert
        v-model="isAlertVisible"
        :color="error ? 'error' : 'success'"
        dismissible
        text
        transition="slide-y-transition"
      >
        {{ message }}
      </v-alert>
      <div class="m-2">
        <v-row class="ml-5 mr-5">
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="$store.state.reductCode.field.label"
              label="Rechercher par libellé"
              outlined
              dense
              placeholder="Rechercher par libellé"
              hide-details="auto"
              clearable
              @input="filterItems"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="$store.state.reductCode.field.code"
              label="Rechercher par code"
              outlined
              dense
              placeholder="Rechercher par code"
              hide-details="auto"
              clearable
              @input="filterItems"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="ml-5 mr-5">
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="$store.state.reductCode.field.user"
              label="Rechercher par propriétaire"
              outlined
              dense
              placeholder="Rechercher par propriétaire"
              hide-details="auto"
              clearable
              @input="filterItems"
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="$store.state.reductCode.field.value"
              label="Rechercher par valeur"
              outlined
              dense
              placeholder="Rechercher par valeur"
              hide-details="auto"
              clearable
              @input="filterItems"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>
    </v-card>
    <v-card>
      <v-data-table
        :loading="isDialogVisible"
        loading-text="Chargement..."
        :headers="headers"
        :items="$store.getters['reductCode/reduct_codes']"
        :items-per-page="$store.getters['reductCode/per_page']"
        :page.sync="$store.getters['reductCode/current_page']"
        dense
        fixed-header
        height="500"
        hide-default-footer
        @page-count="currentPage = $event"
      >
        <template #[`item.color`]="{item}">
          <v-chip
            :color="item.color"
            small
          >
            {{ item.color }}
          </v-chip>
        </template>

        <template #[`item.created_at`]="{item}">
          {{ formattingDate(`${item.created_at}`) }}
        </template>

        <template #[`item.value`]="{item}">
          <v-chip
            :color="new Date(item.end_date) > new Date() ? 'primary' : 'error'"
            small
          >
            {{ item.is_percent === true ? `${item.value} %` : formatAmount(item.value) }}
          </v-chip>
        </template>

        <template #[`item.quantity_left`]="{item}">
          <v-chip
            :color="item.quantity_left > 0 ? 'info' : 'error'"
            small
          >
            {{ item.quantity_left }}
          </v-chip>
        </template>

        <template #[`item.updated_at`]="{item}">
          {{ formattingDate(`${item.updated_at}`) }}
        </template>

        <template #[`item.deleted_at`]="{item}">
          <v-chip
            v-show="item.deleted_at !== null"
            color="error"
            small
          >
            <span class="font-weight-bold">{{ item.deleted_at ? formattingDate(`${item.deleted_at}`) : null }}</span>
          </v-chip>
        </template>
        <template #[`item.end_date`]="{item}">
          {{ formattingDateExHour(`${item.end_date}`) }}
        </template>

        <!-- action -->
        <template #[`item.actions`]="{ item }">
          <buttons-group
            :item="item"
            :lock="false"
            :status="false"
            @view="openDetails(item)"
            @edit="openForm(item)"
            @remove="openItem(item)"
          />
        </template>
      </v-data-table>

      <v-card-text class="pt-2">
        <v-row>
          <v-col
            lg="2"
            cols="3"
          >
            <v-text-field
              :value="$store.state.recovery.field.paginate"
              label="Lignes par page:"
              type="number"
              min="-1"
              max="15"
              hide-details
              @input="getListPerPage"
            ></v-text-field>
          </v-col>

          <v-col
            lg="10"
            cols="9"
            class="d-flex justify-end"
          >
            <v-pagination
              v-model="currentPage"
              total-visible="6"
              :length="lastPage"
              @input="getList"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <reduct-code-form
      :dialog="dialog"
      :object="item"
      @clickout="closeDialog"
    />
    <confirm-dialog
      :message="messageDialog"
      :dialog-delete="dialogDelete"
      @clickout="dialogDelete = false"
      @submitted="deleteItem"
    />
  </div>
</template>

<script>
import {
  getCurrentInstance, onMounted, ref, watch,
} from '@vue/composition-api'
import { useRouter } from '@core/utils'
import { formatDate, formatDateWithoutHour } from '@core/date/dateFunctions'
import {
  mdiAccountLock,
  mdiDeleteOutline, mdiDeleteRestore,
  mdiDotsVertical, mdiEyeOutline, mdiLockOpen,
  mdiPencilOutline, mdiPlus, mdiRefresh,
  mdiSquareEditOutline,
} from '@mdi/js'
import ConfirmDialog from '@core/components/dialogs/ConfirmDialog'
import LoaderDialog from '@core/components/dialogs/LoaderDialog'
import ReductCodeForm from '@core/components/reductCode/ReductCodeForm'
import {intToCurrency, notify} from '@core/dynamicComponents/dynamicComponents'
import CustomToolbar from '@core/components/toolbar/CustomToolbar'
import ButtonsGroup from '@core/components/buttons/ButtonsGroup'
import store from '@/store'

export default {
  name: 'ReductCode',
  components: {
    ButtonsGroup,
    CustomToolbar,
    ReductCodeForm,
    LoaderDialog,
    ConfirmDialog,
  },
  setup() {
    const instance = getCurrentInstance()
    const isLockedColor = {
      false: 'info',
      true: 'success',
    }
    const item = ref({})
    const date = ref(null)
    const message = ref(null)
    const currentPage = ref(null)
    const lastPage = ref(null)
    const error = ref(null)
    const menu = ref(false)
    const messageDialog = ref('')
    const isDialogVisible = ref(false)
    const dialog = ref(false)
    const dialogDelete = ref(false)
    const isAlertVisible = ref(false)
    const picker = ref(null)
    const menuref = ref(null)
    const { router } = useRouter()

    const formatAmount = amount => intToCurrency(store.state.user.lang, {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
    }, amount)

    const save = d => {
      menuref.value.save(d)
    }

    const getList = (page = 1) => {
      isDialogVisible.value = true
      store.dispatch('reductCode/getReductCodesList', { page, field: store.state.reductCode.field }).then(() => {
        currentPage.value = store.getters['reductCode/current_page']
        lastPage.value = store.getters['reductCode/last_page']
        isDialogVisible.value = false
        isAlertVisible.value = false
        // eslint-disable-next-line no-shadow
      }).catch(error => {
        message.value = error.response.data.message
        error.value = error.response.data.message
        isAlertVisible.value = true
      })
    }

    const refreshList = (page = 1) => {
      isDialogVisible.value = true
      store.dispatch('reductCode/refreshReductCodesList', { page, field: store.state.reductCode.field }).then(() => {
        currentPage.value = store.getters['reductCode/current_page']
        lastPage.value = store.getters['reductCode/last_page']
        isDialogVisible.value = false
        isAlertVisible.value = false
        // eslint-disable-next-line no-shadow
      }).catch(error => {
        message.value = error.response.data.message
        error.value = error.response.data.message
        isAlertVisible.value = true
      })
    }

    const closeDialog = () => {
      dialog.value = false
    }

    const loading = () => {
      isDialogVisible.value = !isDialogVisible.value
    }

    const filterItems = () => {
      setTimeout(() => {
        getList()
      }, 1500)
    }

    const getListPerPage = per_page => {
      isDialogVisible.value = true
      store.state.reductCode.field.paginate = per_page
      store.dispatch('reductCode/getReductCodesList', { field: store.state.reductCode.field }).then(() => {
        currentPage.value = store.getters['reductCode/current_page']
        lastPage.value = store.getters['reductCode/last_page']
        isDialogVisible.value = false
        isAlertVisible.value = false
        // eslint-disable-next-line no-shadow
      }).catch(error => {
        message.value = error.response.data.message
        error.value = error.response.data.message
        isAlertVisible.value = true
      })
    }

    const deleteItem = () => {
      isDialogVisible.value = true
      if (item.value.deleted_at === null) {
        store.dispatch('reductCode/deleteReductCode', item.value).then(response => {
          isDialogVisible.value = false
          isAlertVisible.value = false
          dialogDelete.value = false
          notify(true, true, response.message)
        }).catch(err => {
          message.value = err.response.data.message
          error.value = err.response.data.message
          isAlertVisible.value = true
          dialogDelete.value = false
          notify(false, true, err.response.data.message)
        })
      } else {
        store.dispatch('reductCode/restoreReductCode', item.value).then(response => {
          isDialogVisible.value = false
          isAlertVisible.value = false
          dialogDelete.value = false
          notify(true, true, response.message)
        }).catch(err => {
          message.value = err.response.data.message
          error.value = err.response.data.message
          isAlertVisible.value = true
          dialogDelete.value = false
          notify(false, true, err.response.data.message)
        })
      }
    }

    const openForm = val => {
      if (val) {
        val.end_date = new Date(val.end_date).toISOString().substr(0, 10)
        item.value = val
      } else {
        item.value = {
          id: null,
          label: null,
          code: null,
          value: null,
          init_quantity: null,
          quantity_per_user: null,
          is_percent: false,
          end_date: null,
        }
      }
      dialog.value = true
    }

    const formattingDate = date => formatDate(date)

    const formattingDateExHour = date => formatDateWithoutHour(date)

    const closeDelete = () => {
      dialogDelete.value = false
    }

    const deleteItemConfirm = () => {
      this.closeDelete()
    }

    const openItem = object => {
      messageDialog.value = object.deleted_at === null ? `Voulez-vous supprimer le code ${object.label}?` : `Voulez-vous restaurer le code ${object.label}?`
      item.value = object
      dialogDelete.value = true
    }

    onMounted(() => {
      store.state.reductCode.field.user_id = null
      getList(store.getters['reductCode/current_page'])
    })

    const openDetails = item => {
      router.push({ name: 'showreductcode', params: { id: item.id } })
    }

    watch(() => store.state.user.lang, lang => {
      refreshList(store.getters['reductCode/current_page'])
    })

    return {
      formatAmount,
      openDetails,
      deleteItem,
      dialog,
      item,
      messageDialog,
      formattingDateExHour,
      loading,
      closeDialog,
      openForm,
      filterItems,
      openItem,
      getListPerPage,
      deleteItemConfirm,
      closeDelete,
      save,
      getList,
      formattingDate,
      currentPage,
      message,
      lastPage,
      isAlertVisible,
      isDialogVisible,
      dialogDelete,
      error,
      date,
      menu,
      picker,
      menuref,
      headers: [
        { text: 'LABEL', value: 'label' },
        { text: 'CODE', value: 'code' },
        { text: 'VALEUR', value: 'value' },
        { text: 'PROPRIETAIRE', value: 'user.name' },
        { text: 'QUANTITE RESTANTE', value: 'quantity_left' },
        { text: 'DATE DE CREATION', value: 'created_at' },
        { text: 'DATE DE FIN', value: 'end_date' },
        { text: 'DATE DE MISE A JOUR', value: 'updated_at' },
        { text: 'DATE DE SUPPRESSION', value: 'deleted_at' },
        { text: 'ACTIONS', value: 'actions' },
      ],

      // icons
      icons: {
        mdiSquareEditOutline,
        mdiDotsVertical,
        mdiDeleteOutline,
        mdiPencilOutline,
        mdiAccountLock,
        mdiLockOpen,
        mdiEyeOutline,
        mdiRefresh,
        mdiDeleteRestore,
        mdiPlus,
      },
    }
  },
}
</script>

<style scoped>

</style>
